// Печатният документ „Доставка": тип (Фактура / Стокова), номер и плащане в
// заглавието; банерите (`DeliveryDocumentBanners`) и лентата „Плащане"
// (`DeliveryPaymentPanel`); бележката — най-горе в информационната решетка (на
// цяла ширина, печата се); таблицата — на фактура над стокови и редовете на
// стоковите, по група „От стокова разписка …" (с бележката ѝ), а „Общо" е с тях
// (иначе фактура само над стокови излиза празна и с 0.00); футър Анулирай ·
// Редактирай · Приключи без фактура | PDF · Принтирай. Действията по документа
// се показват само когато е отворен от ред и е активен; стокова във фактура не
// се редактира и не се анулира сама.
//
// `@page { size: A4; margin: 0 }` и двойката delivery-doc-paper /
// printing-delivery са НАРОЧНО различни от другите документни модали.
import { Fragment, useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined, EditOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { modalStyles } from "../../../../utils/modalStyles";
import { useViewport } from "../../../../hooks/useViewport";
import { useProducts } from "../../../../hooks/useInventory";
import { useLinkedNotes } from "../../../../hooks/useInventoryDeliveries";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import {
  DANGER_TEXT_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";
import type { Delivery, DeliveryLine } from "../../../../api/inventory";
import { DocumentRevisionsPanel } from "../../components/revisions/DocumentRevisionsPanel";
import { DeliveryPaymentPill, DeliveryTypeBadge } from "./DeliveryBadges";
import { DeliveryDocumentBanners } from "./DeliveryDocumentBanners";
import { DeliveryPaymentPanel } from "./DeliveryPaymentPanel";
import { deliveryGrossOf, deliveryNetOf, docTypeOf, isDeliveryAnnulled, warehouseNamesOf } from "./deliveryMeta";

type Props = {
  open: boolean;
  onClose: () => void;
  delivery: Delivery | null;
  onEdit?: () => void;
  onAnnul?: () => void;
  // отваря друг документ (фактурата на стокова / стокова от фактура)
  onOpenDelivery?: (id: number) => void;
  // плащането и стоковата (подадени само за отворен от ред, активен документ)
  onMarkPaid?: () => void;
  onChangePayment?: () => void;
  onMarkUnpaid?: () => void;
  onDetach?: () => void;
  onCloseWithoutInvoice?: () => void;
  onReopen?: () => void;
  autoPrint?: boolean;
};

const formatDate = (iso: string | null) => (iso ? dayjs(iso).format("DD.MM.YYYY") : "—");

// Печат само на документа: изваждаме .delivery-doc-paper в <body>, маркираме
// body с .printing-delivery (CSS скрива всичко останало), печатаме и връщаме
// node-а на мястото му.
const printDocument = () => {
  const paper = document.querySelector(".delivery-doc-paper") as HTMLElement | null;
  if (!paper || !paper.parentElement) {
    window.print();
    return;
  }
  const parent = paper.parentElement;
  const placeholder = document.createComment("delivery-doc-placeholder");
  parent.insertBefore(placeholder, paper);
  document.body.appendChild(paper);
  document.body.classList.add("printing-delivery");
  const restore = () => {
    document.body.classList.remove("printing-delivery");
    if (placeholder.parentElement) parent.insertBefore(paper, placeholder);
    placeholder.remove();
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  requestAnimationFrame(() => window.print());
};

export const DeliveryDocumentModal = ({
  open,
  onClose,
  delivery,
  onEdit,
  onAnnul,
  onOpenDelivery,
  onMarkPaid,
  onChangePayment,
  onMarkUnpaid,
  onDetach,
  onCloseWithoutInvoice,
  onReopen,
  autoPrint = false,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const { data: products = [] } = useProducts();
  const unitByProduct = new Map(products.map((p) => [p.id, p.unit]));
  const decimalsOf = useUnitDecimals();
  // редовете на обобщените стокови (фактурата носи само техните id-та)
  const linkedNoteDocs = useLinkedNotes(delivery?.linkedNoteIds ?? []);

  useEffect(() => {
    if (!open || !autoPrint || !delivery) return;
    const timer = setTimeout(printDocument, 250);
    return () => clearTimeout(timer);
  }, [open, autoPrint, delivery]);

  if (!delivery) return null;
  const annulled = isDeliveryAnnulled(delivery);
  const type = docTypeOf(delivery);
  const inInvoice = type === "note" && delivery.invoiceId != null;
  const width = isMobile ? undefined : isTablet ? 720 : 920;
  const docNo = delivery.invoiceNumber || t("inventoryPage.noDocNumber");
  const subtitle = [
    delivery.supplierName || "—",
    formatDate(delivery.date),
    warehouseNamesOf(delivery).join(", ") || null,
    delivery.createdByName
      ? t("inventoryPage.enteredBy", { name: delivery.createdByName })
      : null,
    t("inventoryPage.internalNo", { id: delivery.id }),
  ]
    .filter(Boolean)
    .join(" · ");

  const info: { label: string; value: string }[] = [
    { label: t("inventoryPage.colDate"), value: formatDate(delivery.date) },
    {
      label:
        type === "note" ? t("inventoryPage.colNoteNo") : t("inventoryPage.colInvoice"),
      value: delivery.invoiceNumber || "—",
    },
    { label: t("inventoryPage.colSupplier"), value: delivery.supplierName || "—" },
  ];
  const lineRow = (line: DeliveryLine) => (
    <tr key={line.id}>
      <td>{line.productName}</td>
      <td>{line.warehouseName || "—"}</td>
      <td>{line.packagingName || "—"}</td>
      <td>{line.batchNumber || "—"}</td>
      <td>{line.expiryDate ? formatDate(line.expiryDate) : "—"}</td>
      <td className="num">{formatQty(line.quantity, decimalsOf(line.productId))}</td>
      <td>{unitByProduct.get(line.productId) || "—"}</td>
      {/* ед. цена с до 5 знака (Numeric(13,5)); сумата на реда е въведената, а
          цената — изведена от нея */}
      <td className="num">{formatUnitPrice(line.unitPrice)}</td>
      <td className="num">{formatMoney(line.sumNoVat)}</td>
      <td className="num">{formatMoney(line.sumWithVat)}</td>
    </tr>
  );
  const bannerMargin = isMobile ? "mx-4 mt-3.5" : "mx-[22px] mt-3.5";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body.printing-delivery > *:not(.delivery-doc-paper) { display: none !important; }
          body.printing-delivery { background: #fff !important; }
          body.printing-delivery .delivery-doc-paper {
            display: block !important;
            position: static !important;
            box-shadow: none !important;
            padding: 14mm 12mm !important;
          }
        }
        .delivery-doc table { width: 100%; border-collapse: collapse; }
        .delivery-doc th, .delivery-doc td {
          border: 1px solid #C9C5BC; padding: 6px 8px; font-size: 12px;
        }
        .delivery-doc th {
          background: #f3efe8; text-align: left; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.07em; font-size: 10px;
          color: #8a847b;
        }
        .delivery-doc td.num, .delivery-doc th.num { text-align: right; font-variant-numeric: tabular-nums; }
        .delivery-doc .annulled-stamp { color: #b42318; font-weight: 700; letter-spacing: 0.1em; }
        .delivery-doc td.note-section { background: #f7f5f0; font-weight: 600; font-size: 11px; color: #5c574f; }
      `}</style>

      <div className="flex max-h-[90vh] flex-col bg-surface">
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isMobile ? "px-4 py-3.5" : "px-[22px] py-[18px]"
          }`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <DeliveryTypeBadge documentType={delivery.documentType} />
              <span className="text-lg font-semibold leading-[1.2] text-ink">{docNo}</span>
              <DeliveryPaymentPill delivery={delivery} />
            </div>
            <span className="text-[13px] leading-[normal] text-body">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("inventoryPage.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:text-ink"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>

        <DeliveryDocumentBanners
          delivery={delivery}
          marginClass={bannerMargin}
          onOpenDelivery={onOpenDelivery}
          onDetach={onDetach}
          onReopen={onReopen}
        />
        <DeliveryPaymentPanel
          delivery={delivery}
          className={bannerMargin}
          onMarkPaid={onMarkPaid}
          onChange={onChangePayment}
          onMarkUnpaid={onMarkUnpaid}
        />

        <div className={`min-h-0 overflow-y-auto ${isMobile ? "p-4" : "px-[22px] py-4"}`}>
          {/* История на промените — над листа, не се печата */}
          <DocumentRevisionsPanel key={delivery.id} group="deliveries" movementId={delivery.id} />
          <div className="delivery-doc delivery-doc-paper bg-surface text-ink">
            <h2 className="mb-3 mt-0 flex items-center gap-3 text-lg font-extrabold leading-[normal] text-ink">
              {type === "note" ? t("inventoryPage.docHeadingNote") : t("inventoryPage.docHeading")}
              {annulled && (
                <span className="annulled-stamp text-sm">{t("inventoryPage.statusAnnulledStamp")}</span>
              )}
            </h2>
            <div className={`mb-4 grid gap-2.5 ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
              {/* бележката — най-горе, на цяла ширина: вижда се без скрол и при печат */}
              {delivery.note && (
                <div className="col-span-full">
                  <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                    {t("inventoryPage.fieldNote")}
                  </div>
                  <div className="mt-0.5 whitespace-pre-line text-[13px] leading-[1.4] text-ink">
                    {delivery.note}
                  </div>
                </div>
              )}
              {info.map((item) => (
                <div key={item.label}>
                  <div className="text-[9.5px] font-bold uppercase leading-[normal] tracking-[0.6px] text-muted">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[13px] leading-[normal] text-ink">{item.value}</div>
                </div>
              ))}
            </div>
            <table>
              <thead>
                <tr>
                  <th>{t("inventoryPage.colProductName")}</th>
                  <th>{t("inventoryPage.colWarehouse")}</th>
                  <th>{t("inventoryPage.colPackaging")}</th>
                  <th>{t("inventoryPage.colBatch")}</th>
                  <th>{t("inventoryPage.colExpiry")}</th>
                  <th className="num">{t("inventoryPage.colQty")}</th>
                  <th>{t("inventoryPage.colUnit")}</th>
                  <th className="num">{t("inventoryPage.colUnitPrice")}</th>
                  <th className="num">{t("inventoryPage.colNet")}</th>
                  <th className="num">{t("inventoryPage.colGross")}</th>
                </tr>
              </thead>
              <tbody>
                {delivery.lines.map(lineRow)}
                {linkedNoteDocs.notes.map((note) => (
                  <Fragment key={note.id}>
                    <tr>
                      <td colSpan={10} className="note-section">
                        {t("inventoryPage.docNoteSection", {
                          number: note.invoiceNumber ? `№${note.invoiceNumber}` : t("inventoryPage.noDocNumber"),
                          date: formatDate(note.date),
                          id: note.id,
                        })}
                        {note.paidAt
                          ? ` · ${t("inventoryPage.payPanelPaidSeparately", { date: formatDate(note.paidAt) })}`
                          : ""}
                        {note.note ? ` · ${note.note}` : ""}
                      </td>
                    </tr>
                    {note.lines.map(lineRow)}
                  </Fragment>
                ))}
                {linkedNoteDocs.loading && (
                  <tr>
                    <td colSpan={10} className="text-muted">
                      {t("inventoryPage.docNotesLoading")}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={8} className="font-bold">
                    {t("inventoryPage.total")}
                  </td>
                  {/* със стоковите — сумите им идват от BE и без заредените редове */}
                  <td className="num font-extrabold">{formatMoney(deliveryNetOf(delivery))}</td>
                  <td className="num font-extrabold">{formatMoney(deliveryGrossOf(delivery))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          className={`print:hidden flex flex-wrap items-center gap-2 border-t border-shade-2 ${
            isMobile ? "px-4 py-3" : "px-[22px] pb-[18px] pt-3.5"
          }`}
        >
          {!annulled && !inInvoice && onAnnul && (
            <button type="button" onClick={onAnnul} className={DANGER_TEXT_BUTTON_CLASS}>
              {t("inventoryPage.docDelete")}
            </button>
          )}
          {!annulled && !inInvoice && onEdit && (
            <button type="button" onClick={onEdit} className={SECONDARY_BUTTON_CLASS}>
              <EditOutlined />
              {t("inventoryPage.docEdit")}
            </button>
          )}
          {onCloseWithoutInvoice && (
            <button type="button" onClick={onCloseWithoutInvoice} className={SECONDARY_BUTTON_CLASS}>
              {t("inventoryPage.noteClose")}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={printDocument}
            title={t("inventoryPage.pdfHint")}
            className={SECONDARY_BUTTON_CLASS}
          >
            PDF
          </button>
          <button type="button" onClick={printDocument} className={PRIMARY_BUTTON_CLASS}>
            <PrinterOutlined className="text-[15px]" />
            {t("inventoryPage.print")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeliveryDocumentModal;
